import * as z from 'zod';

// One `deliveryOrder` schema feeds the Доставка table row. Enum values are
// stable slugs the UI maps to localized labels + tones via `lib/status`.

/** Tolerant id — the API sends numeric ids; the UI stringifies for routing. */
export const deliveryOrderIdSchema = z.union([z.string(), z.number()]);
export type DeliveryOrderId = z.infer<typeof deliveryOrderIdSchema>;

/**
 * The delivery lifecycle, matching the eight status pills on the Доставка
 * screen. Unknown values fall back to `planned` so a novel backend status
 * degrades gracefully instead of failing the whole list.
 *
 *  planned      — Запланирован (scheduled, awaiting dispatch)
 *  searching    — Поиск курьера (looking for a courier)
 *  notFound     — Не найдено (no courier found)
 *  active       — Текущие (in progress / on the way)
 *  returned     — Возвращено (returned to sender)
 *  completed    — Завершенные (delivered)
 *  cancelled    — Отменено (cancelled)
 */
export const deliveryStatusSchema = z
  .enum([
    'planned',
    'searching',
    'notFound',
    'active',
    'returned',
    'completed',
    'cancelled',
  ])
  .catch('planned');
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

/** A single stop on the route: the pickup point or one of the dropoff points. */
export const deliveryStopSchema = z.object({
  /** `pickup` = origin (the sender); `dropoff` = a recipient address. */
  kind: z.enum(['pickup', 'dropoff']).catch('dropoff'),
  /** Human-readable address line, e.g. "улица Паркент, 215 Ташкент". */
  address: z.string(),
});
export type DeliveryStop = z.infer<typeof deliveryStopSchema>;

export const deliveryOrderSchema = z.object({
  id: deliveryOrderIdSchema,
  /** Human-facing order number shown in the ID column, e.g. "12345678". */
  number: z.string(),
  status: deliveryStatusSchema,
  /** ISO-8601 creation timestamp; rendered as "10:00, 01.01.2025". */
  createdAt: z.string(),
  /**
   * The ordered route stops. The first is the pickup; the rest are dropoffs. A
   * simple order has one pickup + one dropoff; a multi-drop order has several
   * dropoffs and the row collapses/expands.
   */
  stops: z.array(deliveryStopSchema).min(1),
  /** Product (goods) value in so'm — so'm has no minor units. */
  productPrice: z.number().nonnegative(),
  /** Delivery fee in so'm. */
  deliveryPrice: z.number().nonnegative(),
  /** Assigned courier's full name; empty until one is found. */
  courierName: z.string(),
  /** Branch this order was dispatched from (used by the branch filter). */
  branchName: z.string(),
});
export type DeliveryOrder = z.infer<typeof deliveryOrderSchema>;

/** Generic list envelope for a future delivery-orders backend. */
export const deliveryOrderListSchema = z.object({
  data: z.array(deliveryOrderSchema),
});
export type DeliveryOrderList = z.infer<typeof deliveryOrderListSchema>;
